import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMatrixParams } from '@/app/MatrixSwitch';
import type { FirmAccountantRow } from '@/core/models/firm-settings.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { matrixPath } from '@/core/routing/matrix';
import { firmSettingsService } from '@/core/services/firmSettings';
import { Button } from '@/kit/Button';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { PageHeader } from '@/kit/PageHeader';
import { Select } from '@/kit/Select';
import {
  ACCOUNTANT_CHAR_MODE as CHAR_MODE,
  ACCOUNTANT_EXTRA_CHARS as EXTRA_CHARS,
  ACCOUNTANT_TEXT_FIELDS as FIELDS,
  ACCOUNTING_BODY_OTHER,
  NEW_ACCOUNTANT_FORM as NEW_FORM,
  accountingBodyItems,
  accountantFieldInvalid as fieldInvalid,
  accountantPayload,
  isOtherAccountingBody,
  type AccountantForm,
  type AccountantTextField as TextField,
} from './accountantForm';
import { validateForm } from './validateForm';

/** Add/edit a firm accountant. `;id=<base64 id>` (matrix param) selects edit. */
export function AddNewAccountants() {
  const navigate = useNavigate();
  const encoded = useMatrixParams().id;

  const [form, setForm] = useState<AccountantForm>(NEW_FORM);
  const [touched, setTouched] = useState<Set<string>>(() => new Set());
  const [accountingBodies, setAccountingBodies] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const accountantId = useRef<string | null>(null);
  const mounted = useRef(true);

  const startNew = () => {
    setIsEdit(false);
    accountantId.current = null;
    setForm(NEW_FORM);
    setTouched(new Set());
  };

  const populate = (row: FirmAccountantRow) => {
    setIsEdit(true);
    accountantId.current = row.id;
    setForm({
      name: row.name,
      addressLine: row.addressLine ?? '',
      addressLine2: row.addressLine2 ?? '',
      city: row.city ?? '',
      postCode: row.postCode ?? '',
      county: row.county ?? '',
      country: row.country,
      accountingBody: row.accountingBody,
      accountingBodyName: row.accountingBodyName ?? '',
    });
    setTouched(new Set());
  };

  const load = (id: string) => {
    firmSettingsService.getAccountant(id).then(
      (row) => {
        if (mounted.current) populate(row);
      },
      (error) => notifyError(error),
    );
  };

  useEffect(() => {
    mounted.current = true;
    firmSettingsService.getAccountantOptions().then(
      (options) => {
        if (!mounted.current) return;
        setAccountingBodies(options.accountingBodies);
        setCountries(options.countries);
      },
      (error) => notifyError(error),
    );
    return () => {
      mounted.current = false;
    };
  }, []);

  // Angular `route.paramMap.subscribe`: reacts to param changes (new -> edit after a first save).
  useEffect(() => {
    if (encoded) {
      load(atob(encoded));
    } else {
      // Same as the paramMap subscription: a param change resets the form.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      startNew();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encoded]);

  const onClose = () => navigate('/home/settings/accountants');

  // BUG-014: with no guard here, a double-click (or a slow request plus an impatient second click) fired two
  // identical saveAccountant calls. The first created the accountant; the second then legitimately but confusingly
  // collided with the name the first had just inserted, surfacing "Another accountant already uses this name" for
  // what the user experienced as a single, first-time save. AddAccountantDialog already guarded its Save this way.
  const save = (exitAfterSave: boolean) => {
    if (isSaving) return;
    if (!validateForm(FIELDS.every((field) => !fieldInvalid(form, field)))) {
      setTouched(new Set([...FIELDS, 'country', 'accountingBody']));
      return;
    }

    const payload = accountantPayload(form, accountantId.current);

    setIsSaving(true);
    firmSettingsService.saveAccountant(payload).then(
      (row) => {
        if (!mounted.current) return;
        setIsSaving(false);
        notifySuccess(isEdit ? 'Accountant updated successfully' : 'Accountant added successfully');
        if (exitAfterSave) {
          onClose();
        } else if (!isEdit) {
          navigate(matrixPath('/home/settings', 'addaccountants', { id: btoa(row.id) }));
        } else {
          populate(row);
        }
      },
      (error) => {
        if (mounted.current) setIsSaving(false);
        notifyError(error);
      },
    );
  };

  const onCancel = () => {
    if (isEdit && accountantId.current) {
      load(accountantId.current);
    } else {
      startNew();
    }
  };

  const markTouched = (field: string) => setTouched((current) => new Set(current).add(field));
  const setText = (field: TextField, value: string) => setForm((current) => ({ ...current, [field]: value }));

  const textField = (field: TextField, className: string, maxLength: number, required = false) => (
    <InputField
      fieldClassName={className}
      autoComplete="off"
      required={required || undefined}
      maxLength={maxLength}
      charMode={CHAR_MODE[field]}
      extraChars={EXTRA_CHARS[field]}
      value={form[field]}
      invalid={touched.has(field) && fieldInvalid(form, field)}
      onChange={(event) => setText(field, event.target.value)}
      onBlur={() => markTouched(field)}
    />
  );

  return (
    <div className="sidemenu-hide">
      <div className="d-flex h-100">
        {/* ap5-a2: the client side list (which moved the user to a client) does not belong on a firm accountant form. */}
        <div className="form wp-100">
          <div>
            <div className="sidenav-details mb-4">
              {/* Design review 2 Nov 2026: Save / Save and Exit / Cancel Changes are page actions, so they sit top-right in the sticky
                  header; the footer bar is gone. */}
              <PageHeader title={<span className="d-flex align-items-center">
                  <Button className="back-btn back-arrow mr-1" aria-label="back" title="Back" onClick={onClose}>
                      <Icon name="arrow_back" />
                    </Button>
                  {isEdit ? 'Edit Accountant' : 'Add New Accountant'}
                </span>}>
                <Button className="action-button primary-button" busy={isSaving} onClick={() => save(false)}>
                  Save
                </Button>
                <Button className="action-button primary-button" busy={isSaving} onClick={() => save(true)}>
                  Save and Exit
                </Button>
                <Button className="action-button secondary-button" onClick={onCancel}>
                  Cancel Changes
                </Button>
                <Button className="close-btn" aria-label="Close" onClick={onClose}>
                  <Icon name="close" />
                </Button>
              </PageHeader>
              <div className="mt-1">
                <div className="sidenav-body">
                  <form onSubmit={(event) => event.preventDefault()}>
                    <div className="sidenav-body">
                      <div className="sidenav-body-content p-0">
                        <div className="d-flex gap-30">
                          <div className="form-field">
                            <p>
                              Accountant Name<span className="text-red">*</span>
                            </p>
                            {textField('name', 'w-630', 255, true)}
                            {touched.has('name') && fieldInvalid(form, 'name') && (
                              <span className="text-red font-size-12">This field is required.</span>
                            )}
                          </div>
                        </div>
                        <div className="d-flex gap-30">
                          <div className="form-field">
                            <p>Address</p>
                            {textField('addressLine', 'w-630', 500)}
                          </div>
                        </div>
                        <div className="d-flex gap-30">
                          <div className="form-field">
                            <p>Address line 2</p>
                            {textField('addressLine2', 'w-630', 500)}
                          </div>
                        </div>

                        <div className="d-flex gap-30">
                          <div className="form-field">
                            <p>City</p>
                            {textField('city', 'w-300', 100)}
                          </div>
                          <div className="form-field">
                            <p>Post Code</p>
                            {textField('postCode', 'w-300', 20)}
                          </div>
                        </div>

                        <div className="d-flex gap-30">
                          <div className="form-field">
                            <p>County</p>
                            {textField('county', 'w-300', 100)}
                          </div>
                          <div className="form-field">
                            <p>Country</p>
                            <Select
                              className="w-300"
                              items={countries}
                              value={form.country}
                              onChange={(value) =>
                                setForm((current) => ({ ...current, country: value as string | null }))
                              }
                            />
                          </div>
                        </div>

                        <div className="d-flex gap-30">
                          <div className="form-field">
                            <p>Accounting Body</p>
                            <Select
                              className="w-300"
                              items={accountingBodyItems(
                                accountingBodies.map((b) => (b === 'Other' ? ACCOUNTING_BODY_OTHER : b)),
                                form.accountingBody,
                              )}
                              value={
                                isOtherAccountingBody(form.accountingBody) ? ACCOUNTING_BODY_OTHER : form.accountingBody
                              }
                              onChange={(value) =>
                                setForm((current) => ({
                                  ...current,
                                  accountingBody: value as string | null,
                                  accountingBodyName: isOtherAccountingBody(value as string | null)
                                    ? current.accountingBodyName
                                    : '',
                                }))
                              }
                            />
                            {isOtherAccountingBody(form.accountingBody) && (
                              <div className="form-field mt-1">
                                <p>Accounting body name</p>
                                {textField('accountingBodyName', 'w-300', 20)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
